import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtQty, parseNum, toInput, todayISO } from "@/lib/format";
import { Field, btnGhost, btnPrimary, inputCls } from "@/components/app/common";

function Shell({ open, onOpenChange, title, testId, saving, onSave, children }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid={testId} aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display font-medium text-2xl">{title}</DialogTitle></DialogHeader>
        <div className="space-y-4">{children}</div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className={btnPrimary} onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ImpostazioniDialog({ open, onOpenChange, prodotto, onSaved }) {
  const [f, setF] = useState({ gi: "", data: "", sm: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && prodotto) setF({ gi: toInput(prodotto.giacenza_iniziale), data: prodotto.giacenza_iniziale_data || todayISO(), sm: toInput(prodotto.scorta_minima) }); }, [open, prodotto]);
  const save = async () => {
    const gi = parseNum(f.gi) ?? 0, sm = parseNum(f.sm);
    if (gi < 0 || (sm != null && sm < 0)) return toast.error("Le quantità non possono essere negative");
    setSaving(true);
    try { const { data } = await api.put(`/magazzino/${prodotto.id}/impostazioni`, { giacenza_iniziale: gi, giacenza_iniziale_data: f.data, scorta_minima: sm }); toast.success("Impostazioni magazzino salvate"); onOpenChange(false); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="Impostazioni magazzino" testId="impostazioni-dialog" saving={saving} onSave={save}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Giacenza iniziale (pz)"><Input data-testid="giacenza-iniziale-input" inputMode="decimal" value={f.gi} onChange={(e) => setF({ ...f, gi: e.target.value })} className={inputCls} /></Field>
        <Field label="Alla data"><Input type="date" data-testid="giacenza-iniziale-data-input" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} className={inputCls} /></Field>
      </div>
      <Field label="Scorta minima (pz)" hint="Lascia vuoto per non ricevere avvisi di sotto scorta."><Input data-testid="scorta-minima-input" inputMode="decimal" value={f.sm} placeholder="Es. 15" onChange={(e) => setF({ ...f, sm: e.target.value })} className={inputCls} /></Field>
    </Shell>
  );
}

export function RettificaDialog({ open, onOpenChange, prodotto, onSaved }) {
  const [f, setF] = useState({ conteggio: "", data: todayISO(), note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ conteggio: "", data: todayISO(), note: "" }); }, [open]);
  const c = parseNum(f.conteggio);
  const diff = c != null && prodotto ? Math.round((c - prodotto.giacenza) * 1000) / 1000 : null;
  const save = async () => {
    if (c == null || c < 0) return toast.error("Inserisci la quantità contata");
    setSaving(true);
    try { const { data } = await api.post(`/magazzino/${prodotto.id}/rettifica`, { conteggio: c, data: f.data, note: f.note }); toast.success(`Rettifica registrata: ${data.rettifica.quantita > 0 ? "+" : ""}${fmtQty(data.rettifica.quantita)} pz`); onOpenChange(false); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="Rettifica inventario" testId="rettifica-dialog" saving={saving} onSave={save}>
      <p className="text-sm text-[#756B64]">Inserisci la quantità contata fisicamente: il sistema registra la differenza rispetto alla giacenza attuale (<b className="text-[#2C2623] tabular">{fmtQty(prodotto?.giacenza)} pz</b>).</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Quantità contata (pz)"><Input autoFocus data-testid="rettifica-conteggio-input" inputMode="decimal" value={f.conteggio} onChange={(e) => setF({ ...f, conteggio: e.target.value })} className={inputCls} /></Field>
        <Field label="Data conteggio"><Input type="date" data-testid="rettifica-data-input" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} className={inputCls} /></Field>
      </div>
      {diff != null && <div data-testid="rettifica-diff-preview" className={`rounded-xl px-4 py-3 text-sm ${diff === 0 ? "bg-[#FAF7F5] text-[#756B64]" : diff > 0 ? "bg-[#8AA090]/15 text-[#4E6656]" : "bg-[#E07A5F]/10 text-[#A2513C]"}`}>{diff === 0 ? "Nessuna differenza: la giacenza è già corretta." : <>Rettifica: <b className="tabular">{diff > 0 ? "+" : ""}{fmtQty(diff)} pz</b></>}</div>}
      <Field label="Note"><Input data-testid="rettifica-note-input" value={f.note} placeholder="Es. inventario di fine mese" onChange={(e) => setF({ ...f, note: e.target.value })} className={inputCls} /></Field>
    </Shell>
  );
}
